<template>
  <div v-if="isVisible" class="h-full flex flex-col">
    <div class="flex flex-1 min-h-0">
      <!-- Menu lateral -->
      <aside class="bg-slate-800 w-20 flex flex-col items-center py-4 space-y-2 border-r border-slate-700">
        <button
          v-for="layout in mosaicLayouts"
          :key="layout.key"
          @click="selectedLayout = layout.key"
          :class="[
            'w-full py-2 text-xs font-semibold rounded transition-all',
            selectedLayout === layout.key
              ? 'bg-blue-500 text-white shadow'
              : 'text-slate-200 hover:bg-slate-700 hover:text-white'
          ]"
        >
          {{ layout.key.toUpperCase() }}
        </button>
      </aside>
      <!-- Área principal -->
      <main class="flex-1 flex flex-col items-center justify-center p-4 overflow-auto relative">
        <button v-if="!modoPainel" @click="closeModal" class="absolute top-4 right-4 bg-transparent border-none text-slate-400 cursor-pointer p-2 rounded-md transition-all duration-200 hover:bg-white/10 hover:text-slate-100 z-10">
          <X :size="20" />
        </button>
        <div class="flex-1 w-full flex items-center justify-center">
          <div
            class="w-full h-full flex items-center justify-center shadow"
            style="min-height: 300px; min-width: 300px;"
          >
            <div
              class="grid w-full h-full max-w-4xl max-h-[80vh] aspect-square"
              :style="getMosaicGridStyle()"
            >
              <div
                v-for="cellIdx in totalCells"
                :key="cellIdx - 1"
                :data-cell-id="cellIdx - 1"
                :title="getCellImageIndex(cellIdx - 1) !== undefined ? 
                  `Imagem ${getCellImageIndex(cellIdx - 1) + 1} - Clique para alterar, Clique direito para limpar` : 
                  'Clique para selecionar imagem ou duplo clique para imagem atual'"
                :class="[
                  'flex items-center justify-center relative transition-all duration-200 cursor-pointer',
                  selectedCell === (cellIdx - 1) ? 'ring-2 ring-blue-500' : '',
                  getCellImageIndex(cellIdx - 1) !== undefined
                    ? 'bg-white border border-dashed border-slate-500 shadow-md hover:shadow-lg'
                    : 'bg-white border border-dashed border-slate-500 hover:bg-slate-50'
                ]"
                @click="handleCellClick(cellIdx - 1)"
                @dblclick="handleCellDoubleClick(cellIdx - 1)"
                @contextmenu.prevent="clearCell(cellIdx - 1)"
              >
                <div v-if="getCellImageIndex(cellIdx - 1) !== undefined" class="relative w-full h-full flex items-center justify-center overflow-hidden">
                  <div 
                    class="image-container relative w-full h-full"
                    @mousedown="startPan($event, cellIdx - 1)"
                    @mousemove="handlePan($event, cellIdx - 1)"
                    @mouseup="stopPan"
                    @wheel="handleZoom($event, cellIdx - 1)"
                    @mouseleave="stopPan"
                  >
                    <img
                      :src="getCellImageUrl(cellIdx - 1)"
                      :alt="`Imagem ${getCellImageIndex(cellIdx - 1) + 1}`"
                      :style="getImageTransform(cellIdx - 1)"
                      class="object-contain w-full h-full p-2 rounded-lg transition-transform duration-200"
                    >
                  </div>
                  <span class="absolute bottom-1 right-2 bg-white/80 text-slate-500 text-xs px-1.5 py-0.5 rounded shadow font-semibold select-none z-10">
                    {{ getCellImageIndex(cellIdx - 1) + 1 }}
                  </span>
                  <!-- Controles de zoom -->
                  <div class="absolute top-1 right-1 flex gap-1 z-10">
                    <button 
                      @click="resetImageTransform(cellIdx - 1)"
                      class="bg-white/80 text-slate-600 text-xs px-1.5 py-0.5 rounded shadow hover:bg-white transition-colors"
                      title="Resetar zoom e posição"
                    >
                      ↺
                    </button>
                    <button 
                      @click="zoomIn(cellIdx - 1)"
                      class="bg-white/80 text-slate-600 text-xs px-1.5 py-0.5 rounded shadow hover:bg-white transition-colors"
                      title="Zoom +"
                    >
                      +
                    </button>
                    <button 
                      @click="zoomOut(cellIdx - 1)"
                      class="bg-white/80 text-slate-600 text-xs px-1.5 py-0.5 rounded shadow hover:bg-white transition-colors"
                      title="Zoom -"
                    >
                      -
                    </button>
                  </div>
                </div>
                <div v-else class="flex flex-col items-center justify-center text-slate-400 text-xs bg-white">
                  <span class="mb-1">Clique para selecionar</span>
                  <span>ou duplo clique para imagem atual</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
    <!-- Rodapé com abas e controles -->
    <div class="flex flex-col gap-2 border-t border-white/10 bg-slate-700 px-4 pt-2 pb-4">
      <!-- Abas de páginas -->
      <div class="flex items-center gap-1 overflow-x-auto pb-1">
        <template v-for="(page, idx) in pages" :key="idx">
          <button
            :class="[
              'flex items-center gap-1 px-4 py-1 rounded-t-md border-b-2 transition-all duration-150',
              currentPage === idx + 1
                ? 'bg-white text-blue-600 border-blue-500 font-bold shadow'
                : 'bg-slate-200 text-slate-600 border-transparent hover:bg-white/80'
            ]"
            @click="goToPage(idx + 1)"
          >
            <FileText :size="16" class="mr-1" />
            Página {{ idx + 1 }}
            <button v-if="pages.length > 1" @click.stop="removeCurrentPage(idx + 1)" class="ml-2 text-slate-400 hover:text-red-500"><X :size="14" /></button>
          </button>
        </template>
        <button @click="addNewPage" class="ml-2 px-2 py-1 rounded-md bg-green-500 text-white hover:bg-green-600 flex items-center"><Plus :size="16" class="mr-1" /> Nova Página</button>
      </div>

      <!-- Controles de papel e orientação -->
      <div class="flex items-center gap-4 mt-1">
        <div class="flex items-center gap-1">
          <span class="text-xs text-slate-200 mr-1">Formato:</span>
          <button @click="paperFormat = 'A4'" :class="['px-2 py-1 rounded', paperFormat === 'A4' ? 'bg-blue-500 text-white' : 'bg-white text-slate-700']"><FileText :size="14" class="inline mr-1" />A4</button>
          <button @click="paperFormat = 'A3'" :class="['px-2 py-1 rounded', paperFormat === 'A3' ? 'bg-blue-500 text-white' : 'bg-white text-slate-700']"><FileText :size="14" class="inline mr-1" />A3</button>
        </div>
        <div class="flex items-center gap-1">
          <span class="text-xs text-slate-200 mr-1">Orientação:</span>
          <button @click="orientation = 'portrait'" :class="['px-2 py-1 rounded', orientation === 'portrait' ? 'bg-blue-500 text-white' : 'bg-white text-slate-700']"><Layout :size="14" class="inline mr-1 rotate-0" />Retrato</button>
          <button @click="orientation = 'landscape'" :class="['px-2 py-1 rounded', orientation === 'landscape' ? 'bg-blue-500 text-white' : 'bg-white text-slate-700']"><Layout :size="14" class="inline mr-1 rotate-90" />Paisagem</button>
        </div>
      </div>

      <!-- Botões de ação - SEMPRE VISÍVEIS -->
      <div class="flex-shrink-0 pt-4 border-t border-white/10 space-y-3 bg-slate-700 sticky bottom-0">
        <!-- Linha de Ações -->
        <div class="flex justify-between items-center">
          <!-- Ações Secundárias (Esquerda) -->
          <div class="flex items-center space-x-2">
            <button @click="clearAllCells" 
              class="px-3 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800 text-red-800 dark:text-red-200 rounded-md text-sm disabled:opacity-50 flex items-center gap-2 transition-colors"
              title="Limpar todas as células do mosaico">
              <X :size="16" /> 
              <span>Limpar Tudo</span>
            </button>
          </div>

          <!-- Ações Principais (Direita) -->
          <div class="flex items-center space-x-2">
            <button @click="closeModal" 
              class="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-md text-sm font-medium flex items-center gap-2 transition-colors"
              title="Cancelar e fechar o modal">
              <span>Cancelar</span>
            </button>
            
            <button @click="printMosaic" 
              :disabled="!canPrint || isProcessing" 
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-2 transition-colors save-button"
              :title="isProcessing ? 'Processando série de impressão...' : 'Salvar o mosaico de imagens'">
              <Save v-if="!isProcessing" :size="16" /> 
              <div v-else class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" style="width: 16px; height: 16px;"></div>
              <span>{{ isProcessing ? 'Processando...' : 'Salvar Série' }}</span>
            </button>
          </div>
        </div>

        <!-- Informação de status -->
        <div v-if="!canPrint" 
          class="text-xs text-center text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded-md">
          <span>Nenhuma imagem disponível para impressão. Selecione imagens para criar o mosaico.</span>
        </div>
      </div>
    </div>

    <!-- BannerMessage para notificações -->
    <BannerMessage
      v-if="showBannerMessage"
      :type="bannerType"
      :message="bannerMessage"
      @close="showBannerMessage = false"
      style="z-index: 999999;"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, defineExpose, onMounted } from 'vue';
import { X, Plus, FileText, Layout, Save } from 'lucide-vue-next';
import apiClient from '@/services/authService';
import BannerMessage from '@/components/BannerMessage.vue';

const showBannerMessage = ref(false);
const bannerMessage = ref('');
const bannerType = ref('error');

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },
  availableImages: {
    type: Array,
    default: () => []
  },
  currentImageIndex: {
    type: Number,
    default: 0
  },
  paciente: {
    type: Object,
    default: () => ({})
  },
  modoPainel: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'solicitar-selecao-imagem', 'solicitar-imagem-atual']);

// Layouts de mosaico disponíveis (sempre definidos como landscape)
const baseMosaicLayouts = [
  { key: '1x1', rows: 1, cols: 1, description: 'Uma imagem' },
  { key: '1x2', rows: 1, cols: 2, description: 'Duas imagens lado a lado' },
  { key: '2x2', rows: 2, cols: 2, description: 'Quatro imagens em grade' },
  { key: '2x3', rows: 2, cols: 3, description: 'Seis imagens (2 linhas, 3 colunas)' },
  { key: '2x4', rows: 2, cols: 4, description: 'Oito imagens (2 linhas, 4 colunas)' },
  { key: '3x3', rows: 3, cols: 3, description: 'Nove imagens em grade' },
  { key: '4x4', rows: 4, cols: 4, description: 'Dezesseis imagens em grade' },
  { key: '4x5', rows: 4, cols: 5, description: 'Vinte imagens (4 linhas, 5 colunas)' },
  { key: '4x6', rows: 4, cols: 6, description: 'Vinte e quatro imagens (4 linhas, 6 colunas)' },
  { key: '5x6', rows: 5, cols: 6, description: 'Trinta imagens (5 linhas, 6 colunas)' }
];

// Computed layouts de acordo com a orientação
const mosaicLayouts = computed(() => {
  if (orientation.value === 'landscape') {
    return baseMosaicLayouts;
  } else {
    // Inverter linhas e colunas para retrato
    return baseMosaicLayouts.map(layout => ({
      ...layout,
      key: layout.cols + 'x' + layout.rows,
      rows: layout.cols,
      cols: layout.rows,
      description: layout.description.replace(/(linhas|colunas)/g, m => m === 'linhas' ? 'colunas' : 'linhas')
    }));
  }
});

// Estado reativo
const selectedLayout = ref('2x2');
const selectedCell = ref(null); // índice da célula aguardando seleção
const customImages = ref([]); // índices das imagens customizadas
const capturedImages = ref([]); // imagens capturadas do canvas (base64)

// Estado para zoom e pan de cada célula
const cellZoom = ref([]); // zoom de cada célula
const cellPan = ref([]); // posição de pan de cada célula
const cellInteraction = ref(null); // célula atualmente sendo interagida

// Estado para múltiplas páginas
const currentPage = ref(1); // Página atual (sempre inicia na 1)
const pages = ref([]); // Array de páginas, cada página contém os dados das células

// Estado para formato e orientação do papel
const paperFormat = ref('A4'); // 'A4' ou 'A3'
const orientation = ref('landscape'); // 'landscape' ou 'portrait'
const imageQuality = ref(0.4); // Qualidade da imagem (0.2 a 1.0)

// Estado para controle de loading
const isProcessing = ref(false); // Controla se está processando a requisição

// Inicializar arrays com o tamanho correto
function initializeArrays() {
  const layout = mosaicLayouts.value.find(l => l.key === selectedLayout.value);
  const total = layout ? layout.rows * layout.cols : 4;
  
  console.log('Inicializando arrays com tamanho:', total);
  
  // Inicializar a primeira página se não existir
  if (pages.value.length === 0) {
    pages.value = [{
      customImages: Array.from({ length: total }, () => undefined),
      capturedImages: Array.from({ length: total }, () => undefined),
      cellZoom: Array.from({ length: total }, () => 1),
      cellPan: Array.from({ length: total }, () => ({ x: 0, y: 0 }))
    }];
  }
  
  // Garantir que a página atual tenha o tamanho correto
  const currentPageData = pages.value[currentPage.value - 1];
  if (currentPageData) {
    if (currentPageData.customImages.length !== total) {
      currentPageData.customImages = Array.from({ length: total }, (_, i) => currentPageData.customImages[i] ?? undefined);
    }
    if (currentPageData.capturedImages.length !== total) {
      currentPageData.capturedImages = Array.from({ length: total }, (_, i) => currentPageData.capturedImages[i] ?? undefined);
    }
    if (currentPageData.cellZoom.length !== total) {
      currentPageData.cellZoom = Array.from({ length: total }, (_, i) => currentPageData.cellZoom[i] ?? 1);
    }
    if (currentPageData.cellPan.length !== total) {
      currentPageData.cellPan = Array.from({ length: total }, (_, i) => currentPageData.cellPan[i] ?? ({ x: 0, y: 0 }));
    }
  }
  
  // Sincronizar com os arrays reativos atuais
  syncCurrentPageData();
  
  console.log('Arrays inicializados:', {
    totalPages: pages.value.length,
    currentPage: currentPage.value,
    totalCells: total
  });
}

// Função para sincronizar dados da página atual com arrays reativos
function syncCurrentPageData() {
  const currentPageData = pages.value[currentPage.value - 1];
  if (currentPageData) {
    customImages.value = [...currentPageData.customImages];
    capturedImages.value = [...currentPageData.capturedImages];
    cellZoom.value = [...currentPageData.cellZoom];
    cellPan.value = [...currentPageData.cellPan];
  }
}

// Função para salvar dados atuais na página atual
function saveCurrentPageData() {
  const currentPageData = pages.value[currentPage.value - 1];
  if (currentPageData) {
    currentPageData.customImages = [...customImages.value];
    currentPageData.capturedImages = [...capturedImages.value];
    currentPageData.cellZoom = [...cellZoom.value];
    currentPageData.cellPan = [...cellPan.value];
  }
}

// Função para navegar para uma página específica
function goToPage(pageNumber) {
  if (pageNumber < 1 || pageNumber > pages.value.length) return;
  
  // Salvar dados da página atual
  saveCurrentPageData();
  
  // Mudar para a nova página
  currentPage.value = pageNumber;
  
  // Carregar dados da nova página
  syncCurrentPageData();
  
  console.log(`Navegou para página ${pageNumber}`);
}

// Função para adicionar uma nova página
function addNewPage() {
  const layout = mosaicLayouts.value.find(l => l.key === selectedLayout.value);
  const total = layout ? layout.rows * layout.cols : 4;
  
  // Salvar dados da página atual
  saveCurrentPageData();
  
  // Criar nova página
  const newPage = {
    customImages: Array.from({ length: total }, () => undefined),
    capturedImages: Array.from({ length: total }, () => undefined),
    cellZoom: Array.from({ length: total }, () => 1),
    cellPan: Array.from({ length: total }, () => ({ x: 0, y: 0 }))
  };
  
  pages.value.push(newPage);
  
  // Ir para a nova página
  currentPage.value = pages.value.length;
  
  // Carregar dados da nova página
  syncCurrentPageData();
  
  showBanner('success', `Nova página ${currentPage.value} criada`);
  console.log(`Nova página criada: ${currentPage.value}`);
}

// Função para remover uma página específica
function removeCurrentPage(pageIdx) {
  if (pages.value.length <= 1) {
    showBanner('warning', 'Não é possível remover a única página');
    return;
  }
  pages.value.splice(pageIdx - 1, 1);
  if (currentPage.value > pages.value.length) {
    currentPage.value = pages.value.length;
  }
  syncCurrentPageData();
  showBanner('success', `Página removida. Agora na página ${currentPage.value}`);
}

// Inicializar arrays quando o componente é montado
onMounted(() => {
  console.log('Componente PrintSeriesModal montado');
  initializeArrays();
});

// Computed properties
const canPrint = computed(() => {
  const layout = mosaicLayouts.value.find(l => l.key === selectedLayout.value);
  if (!layout) return false;
  
  return props.availableImages.length > 0;
});

const totalCells = computed(() => {
  const layout = mosaicLayouts.value.find(l => l.key === selectedLayout.value);
  return layout ? layout.rows * layout.cols : 4;
});

// Métodos
function closeModal() {
  emit('close');
}

function getMosaicGridStyle() {
  const layout = mosaicLayouts.value.find(l => l.key === selectedLayout.value);
  if (!layout) return {};
  
  return {
    gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
    gridTemplateRows: `repeat(${layout.rows}, 1fr)`
  };
}

function printMosaic() {
  if (!canPrint.value) return;
  
  // Salvar dados da página atual antes de enviar
  saveCurrentPageData();
  
  // Preparar dados para envio ao backend
  const mosaicData = {
    patient: props.paciente,
    layout: selectedLayout.value,
    orientation: orientation.value,
    paperFormat: paperFormat.value,
    imageQuality: imageQuality.value, // Adicionar a qualidade da imagem
    pages: pages.value.map((page, pageIndex) => ({
      pageNumber: pageIndex + 1,
      customImages: page.customImages,
      capturedImages: page.capturedImages,
      cellZoom: page.cellZoom,
      cellPan: page.cellPan
    }))
  };
  
  console.log('Enviando mosaico para o backend:', mosaicData);
  
  // Enviar para o backend
  sendMosaicToBackend(mosaicData);
}

// Função para comprimir imagem base64 antes do envio
function compressImage(base64String, quality = 0.4, maxWidth = 800) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        // Criar canvas para redimensionar e comprimir
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calcular novas dimensões baseadas na qualidade
        let { width, height } = img;
        
        // Ajustar maxWidth baseado na qualidade
        let targetWidth = maxWidth;
        if (quality <= 0.3) {
          targetWidth = 600; // Qualidade baixa: imagem menor
        } else if (quality <= 0.5) {
          targetWidth = 800; // Qualidade média: tamanho padrão
        } else {
          targetWidth = 1000; // Qualidade alta: imagem maior
        }
        
        if (width > targetWidth) {
          height = (height * targetWidth) / width;
          width = targetWidth;
        }
        
        // Definir dimensões do canvas
        canvas.width = width;
        canvas.height = height;
        
        // Desenhar imagem redimensionada
        ctx.drawImage(img, 0, 0, width, height);
        
        // Converter para JPEG com qualidade reduzida
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        
        console.log(`Imagem comprimida: ${(base64String.length / 1024).toFixed(1)}KB → ${(compressedBase64.length / 1024).toFixed(1)}KB (${((1 - compressedBase64.length / base64String.length) * 100).toFixed(1)}% redução)`);
        
        resolve(compressedBase64);
      };
      
      img.onerror = () => {
        console.warn('Erro ao carregar imagem para compressão, retornando original');
        resolve(base64String);
      };
      
      img.src = base64String;
    } catch (error) {
      console.error('Erro na compressão da imagem:', error);
      resolve(base64String);
    }
  });
}

// Função para comprimir todas as imagens capturadas antes do envio
async function compressAllCapturedImages(pages) {
  const compressedPages = [];
  
  for (const page of pages) {
    const compressedPage = { ...page };
    
    // Comprimir imagens capturadas se existirem
    if (page.capturedImages && page.capturedImages.length > 0) {
      const compressedImages = [];
      
      for (const capturedImage of page.capturedImages) {
        if (capturedImage && typeof capturedImage === 'string' && capturedImage.startsWith('data:image')) {
          // Comprimir apenas imagens base64 válidas
          const compressed = await compressImage(capturedImage, imageQuality.value, 800); // Usar imageQuality.value
          compressedImages.push(compressed);
        } else {
          compressedImages.push(capturedImage);
        }
      }
      
      compressedPage.capturedImages = compressedImages;
    }
    
    compressedPages.push(compressedPage);
  }
  
  return compressedPages;
}

// Função para enviar mosaico para o backend
async function sendMosaicToBackend(mosaicData) {
  isProcessing.value = true; // Inicia o loading
  try {
    // Comprimir imagens antes do envio
    console.log('Comprimindo imagens antes do envio...');
    const compressedMosaicData = {
      ...mosaicData,
      pages: await compressAllCapturedImages(mosaicData.pages)
    };
    
    console.log('Imagens comprimidas, enviando para o backend...');
    
    const response = await apiClient.post('/api/v1/pacs/create_print_series', compressedMosaicData);
    
    // Verificar se a resposta é bem-sucedida (status 200-299)
    if (response.status >= 200 && response.status < 300) {
      const result = response.data; // apiClient já retorna os dados parseados
      
      if (result.success || result.message) {
        showBanner('success', result.message || 'Série de impressão criada com sucesso!');
        console.log('Série PRINT processada:', result);
      } else {
        throw new Error(result.error || 'Erro desconhecido ao processar a série');
      }
    } else {
      throw new Error(`Erro HTTP: ${response.status} - ${response.statusText}`);
    }
    
  } catch (error) {
    console.error('Erro ao enviar mosaico:', error);
    showBanner('error', `Erro ao criar série: ${error.message}`);
  } finally {
    isProcessing.value = false; // Finaliza o loading
  }
}

// Watcher para ajustar o layout ao mudar a orientação
watch(orientation, () => {
  // Sempre busque o layout base (landscape) pelo número de células
  const baseLayout = baseMosaicLayouts.find(l => l.key === selectedLayout.value) ||
    baseMosaicLayouts.find(l => l.rows * l.cols === totalCells.value) ||
    baseMosaicLayouts[0];
  if (!baseLayout) return;
  const baseRows = baseLayout.rows;
  const baseCols = baseLayout.cols;
  const total = baseRows * baseCols;

  // No novo conjunto de layouts (invertido ou não), buscar o layout "espelho"
  const newLayouts = mosaicLayouts.value;
  // Tenta encontrar o layout invertido (linhas <-> colunas, mesmo número de células)
  const inverted = newLayouts.find(l => l.rows * l.cols === total && l.rows === baseCols && l.cols === baseRows);
  if (inverted) {
    selectedLayout.value = inverted.key;
    return;
  }
  // Se não existir o espelho exato, pega o layout mais próximo pelo número de células
  const closest = newLayouts.reduce((acc, l) => {
    const diff = Math.abs(l.rows * l.cols - total);
    if (acc === null || diff < acc.diff) {
      return { key: l.key, diff };
    }
    return acc;
  }, null);
  if (closest) {
    selectedLayout.value = closest.key;
  } else {
    selectedLayout.value = newLayouts[0].key;
  }
});

// Atualiza o tamanho do array customImages ao mudar o layout
watch(selectedLayout, () => {
  console.log('Layout alterado para:', selectedLayout.value);
  
  // Salvar dados da página atual antes de mudar o layout
  saveCurrentPageData();
  
  // Atualizar todas as páginas para o novo layout
  const layout = mosaicLayouts.value.find(l => l.key === selectedLayout.value);
  const newTotal = layout ? layout.rows * layout.cols : 4;
  
  pages.value = pages.value.map(page => ({
    customImages: Array.from({ length: newTotal }, (_, i) => page.customImages[i] ?? undefined),
    capturedImages: Array.from({ length: newTotal }, (_, i) => page.capturedImages[i] ?? undefined),
    cellZoom: Array.from({ length: newTotal }, (_, i) => page.cellZoom[i] ?? 1),
    cellPan: Array.from({ length: newTotal }, (_, i) => page.cellPan[i] ?? ({ x: 0, y: 0 }))
  }));
  
  // Sincronizar dados da página atual
  syncCurrentPageData();
  
  selectedCell.value = null;
});

// Watcher adicional para garantir sincronização dos arrays
watch(totalCells, (newTotal, oldTotal) => {
  console.log('Total de células alterado:', { oldTotal, newTotal });
  if (newTotal !== oldTotal) {
    // Salvar dados da página atual antes de atualizar
    saveCurrentPageData();
    
    // Atualizar todas as páginas para o novo total
    pages.value = pages.value.map(page => ({
      customImages: Array.from({ length: newTotal }, (_, i) => page.customImages[i] ?? undefined),
      capturedImages: Array.from({ length: newTotal }, (_, i) => page.capturedImages[i] ?? undefined),
      cellZoom: Array.from({ length: newTotal }, (_, i) => page.cellZoom[i] ?? 1),
      cellPan: Array.from({ length: newTotal }, (_, i) => page.cellPan[i] ?? ({ x: 0, y: 0 }))
    }));
    
    // Sincronizar dados da página atual
    syncCurrentPageData();
  }
});

// Expor método para o parent
function atribuirImagemNaCelula(indexImagem, cellIdx = null, capturedImage = null) {
  console.log('PrintSeriesModal: Atribuindo imagem à célula');
  console.log('Índice da imagem:', indexImagem);
  console.log('Índice da célula:', cellIdx);
  console.log('Célula selecionada:', selectedCell.value);
  console.log('Imagem capturada:', capturedImage ? 'Sim' : 'Não');
  console.log('Layout atual:', selectedLayout.value);
  console.log('Total de células:', totalCells.value);
  console.log('Array customImages:', customImages.value);
  console.log('Array capturedImages:', capturedImages.value);
  
  // Garantir que os arrays estejam inicializados
  if (customImages.value.length !== totalCells.value || capturedImages.value.length !== totalCells.value) {
    console.log('Arrays não sincronizados, inicializando...');
    initializeArrays();
  }
  
  const targetCell = cellIdx !== null ? cellIdx : selectedCell.value;
  console.log('Célula alvo:', targetCell);
  console.log('Validação:', {
    targetCell,
    isNotNull: targetCell !== null,
    isGreaterOrEqualZero: targetCell >= 0,
    isLessThanTotal: targetCell < totalCells.value,
    totalCells: totalCells.value
  });
  
  if (targetCell !== null && targetCell >= 0 && targetCell < totalCells.value) {
    customImages.value[targetCell] = indexImagem;
    
    // Se temos uma imagem capturada do canvas, armazená-la
    if (capturedImage) {
      capturedImages.value[targetCell] = capturedImage;
      console.log('Imagem capturada armazenada na célula:', targetCell);
    }
    
    // Salvar dados na página atual
    saveCurrentPageData();
    
    console.log('Imagem atribuída com sucesso à célula:', targetCell);
    
    // Feedback visual
    const cell = document.querySelector(`[data-cell-id="${targetCell}"]`);
      if (cell) {
        cell.style.backgroundColor = '#10b981';
        cell.style.transform = 'scale(1.05)';
        cell.style.transition = 'all 0.3s ease';
        setTimeout(() => {
          cell.style.backgroundColor = '';
          cell.style.transform = 'scale(1)';
        }, 500);
      }
      
      // Limpar seleção se foi atribuída à célula selecionada
      if (cellIdx === null && selectedCell.value !== null) {
        selectedCell.value = null;
      }
    } else {
      console.error('Célula inválida para atribuição:', targetCell);
      console.error('Detalhes da validação:', {
        targetCell,
        totalCells: totalCells.value,
        layout: selectedLayout.value,
        condition1: targetCell !== null,
        condition2: targetCell >= 0,
        condition3: targetCell < totalCells.value
      });
      showBanner('error', 'Erro: Célula inválida');
    }
  }

  // Função para exibir banners
  const showBanner = (type, message) => {
    bannerType.value = type;
    bannerMessage.value = message;
    showBannerMessage.value = true;
  };

  // Ao clicar em uma célula
  function handleCellClick(cellIdx) {
    console.log('Clique simples na célula:', cellIdx);
    console.log('Total de células:', totalCells.value);
    console.log('Índice válido:', cellIdx >= 0 && cellIdx < totalCells.value);
    
    if (cellIdx >= 0 && cellIdx < totalCells.value) {
      selectedCell.value = cellIdx;
      emit('solicitar-selecao-imagem');
    } else {
      console.error('Tentativa de selecionar célula inválida:', cellIdx);
      showBanner('error', 'Erro: Célula inválida');
    }
  }

  // Função para obter o índice da imagem para cada célula
  function getCellImageIndex(cellIdx) {
    // Retornar apenas imagens customizadas (selecionadas manualmente)
    if (customImages.value[cellIdx] !== undefined) return customImages.value[cellIdx];
    return undefined; // Não usar imagens automáticas
  }

  // Função para obter a URL da imagem (capturada ou original)
  function getCellImageUrl(cellIdx) {
    // Se temos uma imagem capturada, usar ela
    if (capturedImages.value[cellIdx]) {
      return capturedImages.value[cellIdx];
    }
    
    // Senão, usar a imagem original
    const imageIndex = getCellImageIndex(cellIdx);
    if (imageIndex !== undefined) {
      return props.availableImages[imageIndex]?.imageUrl || props.availableImages[imageIndex]?.thumbnail || 'https://via.placeholder.com/60/333/FFFFFF?text=Img';
    }
    
    return null;
  }

  // Função para obter a transformação da imagem (zoom e pan)
  function getImageTransform(cellIdx) {
    const zoom = cellZoom.value[cellIdx] || 1;
    const pan = cellPan.value[cellIdx] || { x: 0, y: 0 };
    
    return {
      transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
      transformOrigin: 'center center'
    };
  }

  // Funções de zoom
  function zoomIn(cellIdx) {
    const currentZoom = cellZoom.value[cellIdx] || 1;
    const newZoom = Math.min(currentZoom * 1.2, 5); // Máximo 5x zoom
    cellZoom.value[cellIdx] = newZoom;
    saveCurrentPageData();
  }

  function zoomOut(cellIdx) {
    const currentZoom = cellZoom.value[cellIdx] || 1;
    const newZoom = Math.max(currentZoom / 1.2, 0.1); // Mínimo 0.1x zoom
    cellZoom.value[cellIdx] = newZoom;
    saveCurrentPageData();
  }

  function resetImageTransform(cellIdx) {
    cellZoom.value[cellIdx] = 1;
    cellPan.value[cellIdx] = { x: 0, y: 0 };
    saveCurrentPageData();
  }

  // Funções de pan
  let isPanning = false;
  let lastPanPosition = { x: 0, y: 0 };

  function startPan(event, cellIdx) {
    if (event.button !== 0) return; // Apenas botão esquerdo
    
    isPanning = true;
    cellInteraction.value = cellIdx;
    lastPanPosition = { x: event.clientX, y: event.clientY };
    event.preventDefault();
  }

  function handlePan(event, cellIdx) {
    if (!isPanning || cellInteraction.value !== cellIdx) return;
    
    const deltaX = event.clientX - lastPanPosition.x;
    const deltaY = event.clientY - lastPanPosition.y;
    
    const currentPan = cellPan.value[cellIdx] || { x: 0, y: 0 };
    cellPan.value[cellIdx] = {
      x: currentPan.x + deltaX,
      y: currentPan.y + deltaY
    };
    
    lastPanPosition = { x: event.clientX, y: event.clientY };
    event.preventDefault();
    
    // Salvar dados na página atual
    saveCurrentPageData();
  }

  function stopPan() {
    isPanning = false;
    cellInteraction.value = null;
  }

  // Função de zoom com scroll
  function handleZoom(event, cellIdx) {
    event.preventDefault();
    
    const delta = event.deltaY > 0 ? 0.9 : 1.1;
    const currentZoom = cellZoom.value[cellIdx] || 1;
    const newZoom = Math.max(0.1, Math.min(5, currentZoom * delta));
    
    cellZoom.value[cellIdx] = newZoom;
    saveCurrentPageData();
  }

  function handleCellDoubleClick(cellIdx) {
    console.log('=== DEBUG DUPLO CLIQUE ===');
    console.log('Célula clicada:', cellIdx);
    console.log('Tipo da célula:', typeof cellIdx);
    console.log('Layout atual:', selectedLayout.value);
    console.log('Total de células (computed):', totalCells.value);
    console.log('Array customImages:', customImages.value);
    console.log('Array capturedImages:', capturedImages.value);
    console.log('Índice da imagem atual:', props.currentImageIndex);
    console.log('Imagens disponíveis:', props.availableImages.length);
    console.log('Validação da célula:', {
      cellIdx,
      totalCells: totalCells.value,
      isValid: cellIdx >= 0 && cellIdx < totalCells.value,
      range: `0 a ${totalCells.value - 1}`
    });
    
    // Verificar se a célula é válida antes de emitir
    if (cellIdx < 0 || cellIdx >= totalCells.value) {
      console.error('Célula inválida detectada antes da emissão:', {
        cellIdx,
        totalCells: totalCells.value,
        isValid: cellIdx >= 0 && cellIdx < totalCells.value
      });
      showBanner('error', `Erro: Célula ${cellIdx + 1} não existe no layout atual`);
      return;
    }
    
    console.log('✅ Célula válida, emitindo evento...');
    
    // Emitir o evento para solicitar a imagem atual
    emit('solicitar-imagem-atual', cellIdx);
    
    // Feedback visual temporário
    const cell = document.querySelector(`[data-cell-id="${cellIdx}"]`);
    if (cell) {
      cell.style.transform = 'scale(0.95)';
      cell.style.transition = 'transform 0.1s ease';
      setTimeout(() => {
        cell.style.transform = 'scale(1)';
      }, 100);
    }
  }

  function clearAllCells() {
    customImages.value = Array.from({ length: totalCells.value }, () => undefined);
    capturedImages.value = Array.from({ length: totalCells.value }, () => undefined);
    cellZoom.value = Array.from({ length: totalCells.value }, () => 1);
    cellPan.value = Array.from({ length: totalCells.value }, () => ({ x: 0, y: 0 }));
    selectedCell.value = null;
    saveCurrentPageData();
    showBanner('success', 'Todas as células foram limpas');
  }

  function clearCell(cellIdx) {
    console.log('Limpando célula:', cellIdx);
    console.log('Total de células:', totalCells.value);
    console.log('Índice válido:', cellIdx >= 0 && cellIdx < totalCells.value);
    
    if (cellIdx >= 0 && cellIdx < totalCells.value) {
      customImages.value[cellIdx] = undefined;
      capturedImages.value[cellIdx] = undefined;
      cellZoom.value[cellIdx] = 1;
      cellPan.value[cellIdx] = { x: 0, y: 0 };
      selectedCell.value = null;
      saveCurrentPageData();
      showBanner('success', `Célula ${cellIdx + 1} foi limpa`);
    } else {
      console.error('Tentativa de limpar célula inválida:', cellIdx);
      showBanner('error', 'Erro: Célula inválida');
    }
  }

  // Função para selecionar uma imagem específica da série
  function selectSeriesImage(cellIdx, imageIndex) {
    if (cellIdx >= 0 && cellIdx < totalCells.value && imageIndex >= 0 && imageIndex < props.availableImages.length) {
      customImages.value[cellIdx] = imageIndex;
      saveCurrentPageData();
    }
  }

  defineExpose({ atribuirImagemNaCelula, selectSeriesImage });
</script>

<style scoped>
/* Transições para o painel deslizante */
.slide-print-panel-enter-active, .slide-print-panel-leave-active {
  transition: all 0.3s cubic-bezier(.4,0,.2,1);
}

.slide-print-panel-enter-from, .slide-print-panel-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.slide-print-panel-enter-to, .slide-print-panel-leave-from {
  transform: translateX(0);
  opacity: 1;
}

/* Responsividade para dispositivos móveis */
@media (max-width: 768px) {
  .slide-print-panel-enter-from, .slide-print-panel-leave-to {
    transform: translateX(100%);
  }
}

@media (max-width: 640px) {
  .slide-print-panel-enter-from, .slide-print-panel-leave-to {
    transform: translateX(100%);
  }
}

/* Estilos para notificações */
.print-notification {
  transform: translateX(100%);
  opacity: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Estilos para zoom e pan */
.image-container {
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.image-container:active {
  cursor: grabbing;
}

.image-container img {
  pointer-events: none;
  will-change: transform;
}

/* Controles de zoom */
.image-container button {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.image-container:hover button {
  opacity: 1;
}

/* Indicador de zoom ativo */
.image-container[data-zoomed="true"] {
  cursor: grab;
}

.image-container[data-zoomed="true"]:active {
  cursor: grabbing;
}

/* [NOVO] Estilos para o rodapé com botões */
.flex-shrink-0.pt-4.border-t.border-white\/10.space-y-3.bg-slate-700.sticky.bottom-0 {
  backdrop-filter: blur(8px);
  background-color: rgba(51, 65, 85, 0.95);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10;
}

/* Garantir que os botões tenham espaçamento adequado */
.flex.items-center.space-x-2 > button {
  min-width: fit-content;
  white-space: nowrap;
}

/* Melhorar a aparência dos botões secundários */
.bg-red-100.hover\:bg-red-200.dark:bg-red-900.dark:hover\:bg-red-800.text-red-800.dark:text-red-200 {
  border: 1px solid rgba(239, 68, 68, 0.2);
}

/* Melhorar a aparência dos botões principais */
.bg-blue-600.hover\:bg-blue-700 {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.bg-gray-200.hover\:bg-gray-300.dark:bg-gray-700.dark:hover\:bg-gray-600 {
  border: 1px solid rgba(156, 163, 175, 0.2);
}

/* Garantir que o container principal tenha altura adequada */
.flex.flex-1.min-h-0 {
  min-height: 0;
  overflow: hidden;
}

/* Garantir que o conteúdo principal tenha scroll quando necessário */
.flex-1.flex.flex-col.items-center.justify-center.p-4.overflow-auto.relative {
  overflow-y: auto;
  min-height: 0;
}

/* Melhorar a aparência das abas */
.flex.items-center.gap-1.overflow-x-auto.pb-1 {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.flex.items-center.gap-1.overflow-x-auto.pb-1::-webkit-scrollbar {
  height: 4px;
}

.flex.items-center.gap-1.overflow-x-auto.pb-1::-webkit-scrollbar-track {
  background: transparent;
}

.flex.items-center.gap-1.overflow-x-auto.pb-1::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 2px;
}

/* Melhorar a aparência dos controles de formato e orientação */
.flex.items-center.gap-4.mt-1 {
  flex-wrap: wrap;
  gap: 1rem;
}

/* Garantir que os botões de formato e orientação sejam responsivos */
.flex.items-center.gap-1 > button {
  min-width: 60px;
  text-align: center;
}

/* Melhorar a aparência da informação de status */
.text-xs.text-center.text-amber-600.dark:text-amber-400.bg-amber-50.dark:bg-amber-900\/20.p-2.rounded-md {
  border: 1px solid rgba(245, 158, 11, 0.2);
  margin-top: 0.5rem;
}

/* Garantir que o layout seja responsivo */
@media (max-width: 768px) {
  .flex.justify-between.items-center {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .flex.items-center.space-x-2 {
    justify-content: center;
  }
  
  .flex.items-center.gap-4.mt-1 {
    justify-content: center;
  }
}

/* Estilos para o botão durante processamento */
button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  transform: none !important;
}

button:disabled:hover {
  background-color: inherit !important;
  transform: none !important;
}

/* Garantir que o botão mantenha o mesmo tamanho */
.flex.items-center.space-x-2 > button {
  min-width: fit-content;
  white-space: nowrap;
  width: auto;
  height: auto;
}

/* Garantir que o spinner tenha o mesmo tamanho do ícone */
.animate-spin {
  animation: spin 1s linear infinite;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* Garantir que o ícone e spinner tenham o mesmo tamanho */
.flex.items-center.gap-2 > svg,
.flex.items-center.gap-2 > .animate-spin {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* Classe específica para o botão de salvar */
.save-button {
  min-width: 140px; /* Largura mínima fixa */
  height: 40px; /* Altura fixa */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.save-button > * {
  flex-shrink: 0;
}

.save-button > span {
  white-space: nowrap;
  min-width: 80px; /* Largura mínima para o texto */
  text-align: center;
}

/* Animação do spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>